export type ServiceStatus = 'up' | 'down' | 'degraded' | 'pending' | 'maintenance';

export type ServiceType = 'http' | 'tcp' | 'dns' | 'ping' | 'heartbeat' | 'group';
export const SERVICE_TYPES: ServiceType[] = ['http', 'tcp', 'dns', 'ping', 'heartbeat', 'group'];

export type DnsRecordType = 'A' | 'AAAA' | 'CNAME' | 'MX' | 'TXT';
export const DNS_RECORD_TYPES: DnsRecordType[] = ['A', 'AAAA', 'CNAME', 'MX', 'TXT'];

export type AssertionOp = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'exists';

/** One expectation about an HTTP response; a service is up when all of them pass. */
export type Assertion =
  | { type: 'status'; value: string }
  | { type: 'body_contains'; value: string; negate?: boolean }
  | { type: 'json_path'; path: string; op: AssertionOp; value?: string | number | boolean | null }
  | { type: 'header'; name: string; op: 'eq' | 'contains' | 'exists'; value?: string }
  | { type: 'latency'; max_ms: number };

export interface AssertionResult {
  assertion: Assertion;
  passed: boolean;
  actual: string | null;
  message: string;
}

export interface CheckTimings {
  dns: number | null;
  connect: number | null;
  tls: number | null;
  ttfb: number | null;
  total: number;
}

export interface TlsSummary {
  subject: string | null;
  issuer: string | null;
  valid_from: string | null;
  valid_to: string | null;
  days_remaining: number | null;
  alt_names: string[];
  authorized: boolean | null;
  authorization_error: string | null;
}

/** What a check learned beyond up/down. */
export interface CheckDetails {
  type: ServiceType;
  resolved_ip?: string | null;
  timings?: CheckTimings | null;
  tls?: TlsSummary | null;
  assertions?: AssertionResult[];
  body_snippet?: string | null;
  body_bytes?: number | null;
  body_truncated?: boolean;
  content_type?: string | null;
  redirects?: string[];
  final_url?: string | null;
  records?: string[];
  resolver?: string | null;
  ping?: { ttl: number | null; packet_loss: number | null } | null;
  heartbeat?: { last_ping_at: string | null; deadline_at: string | null; late_by_seconds: number | null; duration_ms: number | null } | null;
  children?: { id: number; name: string; status: ServiceStatus }[];
  error_code?: string | null;
}

export interface CheckTypeInfo {
  type: ServiceType;
  label: string;
  description: string;
  available: boolean;
  reason: string | null;
}

export interface Service {
  id: number;
  name: string;
  /** Stable id for config-as-code; null for services created in the UI. */
  slug: string | null;
  type: ServiceType;
  /** Canonical target: the URL for http, `tcp://host:port`, `dns://name/TYPE`, `ping://host`, `push://token`, `group://id`. */
  url: string;
  method: string;
  interval: number;
  timeout: number;
  headers: Record<string, string> | null;
  body: string | null;
  /** Legacy single status; the first status assertion is the source of truth. */
  expected_status: number;
  assertions: Assertion[];
  auth_type: string | null;
  auth_value: string | null;
  // HTTP transport
  follow_redirects: boolean;
  max_redirects: number;
  tls_verify: boolean;
  ip_version: '4' | '6' | null;
  resolver: string | null;
  client_cert: string | null;
  /** Masked on read. */
  client_key: string | null;
  // Typed targets
  host: string | null;
  port: number | null;
  dns_record_type: DnsRecordType | null;
  dns_expected: string | null;
  /** Heartbeat: seconds past `interval` before a missing ping counts as a failure. */
  heartbeat_grace: number;
  push_token: string | null;
  last_heartbeat_at: string | null;
  heartbeat_started_at: string | null;
  heartbeat_duration_ms: number | null;
  heartbeat_last_signal: 'ok' | 'fail' | null;
  group_id: number | null;
  group_mode: 'all' | 'any';
  // TLS certificate
  cert_expiry_days: number | null;
  cert_expires_at: string | null;
  cert_issuer: string | null;
  cert_subject: string | null;
  last_check_details?: CheckDetails | null;
  sla_target: number | null;
  tags: string[];
  enabled: boolean;
  public: boolean;
  /** Confirmed status — only changes once the thresholds below are met. */
  status: ServiceStatus;
  last_checked_at: string | null;
  /** Quick re-checks after a failed scheduled check, `retry_interval` seconds apart. */
  retries: number;
  retry_interval: number;
  /** Consecutive failed checks before `status` becomes down/degraded. */
  down_after: number;
  /** Consecutive passing checks before `status` returns to up. */
  up_after: number;
  /** Latency (ms) above which a passing check is degraded. null inherits the global default; 0 disables. */
  degraded_threshold_ms: number | null;
  consecutive_failures: number;
  consecutive_successes: number;
  /** Raw outcome of the most recent check, before thresholds. */
  last_check_status: ServiceStatus | null;
  status_before_maintenance: ServiceStatus | null;
  created_at: string;
  updated_at: string;
  /** Time-weighted from status spans; null means no history in the window, not 100. */
  uptime_24h?: number | null;
  uptime_7d?: number | null;
  uptime_30d?: number | null;
  avg_response_time?: number | null;
  group_ids?: number[];
  recent_checks?: Check[];
  next_check_at?: string | null;
  /** Detail view only. */
  push_url?: string | null;
  heartbeat_deadline_at?: string | null;
  children?: { id: number; name: string; status: ServiceStatus }[];
  last_error?: Check | null;
}

/** What a create/update sends: the editable subset, without the server-computed state. */
export type ServiceInput = Partial<Omit<Service,
  'id' | 'status' | 'last_checked_at' | 'consecutive_failures' | 'consecutive_successes'
  | 'last_check_status' | 'status_before_maintenance' | 'created_at' | 'updated_at'
  | 'uptime_24h' | 'uptime_7d' | 'uptime_30d' | 'avg_response_time' | 'recent_checks' | 'next_check_at'
  | 'push_token' | 'last_heartbeat_at' | 'heartbeat_started_at' | 'heartbeat_duration_ms' | 'heartbeat_last_signal'
  | 'cert_expires_at' | 'cert_issuer' | 'cert_subject' | 'last_check_details'
  | 'push_url' | 'heartbeat_deadline_at' | 'children' | 'last_error'
>>;

export interface Check {
  id: number;
  service_id: number;
  status: ServiceStatus;
  response_time: number | null;
  status_code: number | null;
  error_message: string | null;
  /** 1 for a scheduled check, 2+ for the quick retries that followed a failure. */
  attempt: number;
  in_maintenance: boolean;
  /** Diagnostics, kept on failed checks. */
  details: CheckDetails | null;
  timestamp: string;
}

export interface CheckHourly {
  id: number;
  service_id: number;
  hour: string;
  /** NULL when no check in the hour returned a response time. */
  avg_response_time: number | null;
  min_response_time: number | null;
  max_response_time: number | null;
  p50_response_time: number | null;
  p95_response_time: number | null;
  p99_response_time: number | null;
  /** Checks in the hour that carried a response time. 0 on pre-006 rows. */
  response_count: number;
  check_count: number;
  success_count: number;
  failure_count: number;
}

export interface Alert {
  id: number;
  service_id: number;
  type: 'down' | 'degraded' | 'recovery';
  previous_status: ServiceStatus;
  new_status: ServiceStatus;
  message: string;
  resolved_at: string | null;
  created_at: string;
}

export interface Group {
  id: number;
  name: string;
  slug?: string | null;
  description: string | null;
  sort_order: number;
  service_ids?: number[];
  created_at: string;
  updated_at: string;
}

export type NotificationEvent = 'down' | 'degraded' | 'recovery' | 'reminder' | 'escalation' | 'maintenance' | 'cert_expiring' | 'heartbeat_missed';
export const NOTIFICATION_EVENTS: NotificationEvent[] = ['down', 'degraded', 'recovery', 'reminder', 'escalation', 'maintenance', 'cert_expiring', 'heartbeat_missed'];

export type TargetType = 'all' | 'service' | 'group' | 'tag';

/** Where a channel or maintenance window applies. `target_id` is a service/group id as text, a tag, or '' for `all`. */
export interface NotificationTarget {
  target_type: TargetType;
  target_id: string;
}

export interface NotificationChannel {
  id: number;
  /** Provider type: webhook, slack, discord, teams, telegram, ntfy, gotify, pushover, email, apprise, browser. */
  type: string;
  name: string;
  slug?: string | null;
  /** Provider settings. Credentials come back masked (`••••••••`, or `https://host/…` for URLs). */
  config: Record<string, unknown>;
  events: NotificationEvent[];
  enabled: boolean;
  /** Empty means every service. */
  targets: NotificationTarget[];
  created_at: string;
  updated_at: string;
}

export interface NotificationChannelInput {
  type?: string;
  name?: string;
  config?: Record<string, unknown>;
  events?: NotificationEvent[];
  enabled?: boolean;
  targets?: NotificationTarget[];
}

export interface NotificationType {
  type: string;
  label: string;
}

export interface NotificationTestResult {
  success: boolean;
  status_code?: number;
  error?: string;
}

export interface NotificationDelivery {
  id: number;
  channel_id: number;
  incident_id: number | null;
  service_id: number | null;
  event: string;
  status: 'success' | 'failed' | 'pending';
  response_code: number | null;
  response_body: string | null;
  error_message: string | null;
  attempt: number;
  next_retry_at: string | null;
  created_at: string;
}

export interface MaintenanceRecurrence {
  /** 0 = Sunday … 6 = Saturday, in the window's timezone. */
  weekdays: number[];
  /** HH:MM, 24-hour, in the window's timezone. end < start means it crosses midnight. */
  start_time: string;
  end_time: string;
}

export interface MaintenanceWindow {
  id: number;
  name: string;
  description: string | null;
  enabled: boolean;
  /** One-off bounds as SQLite UTC (`YYYY-MM-DD HH:MM:SS`); null for recurring windows. */
  start_at: string | null;
  end_at: string | null;
  recurrence: MaintenanceRecurrence | null;
  timezone: string;
  created_by: number | null;
  targets: NotificationTarget[];
  /** Computed by the server: is the window active right now? */
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceWindowInput {
  name?: string;
  description?: string | null;
  enabled?: boolean;
  start_at?: string | null;
  end_at?: string | null;
  recurrence?: MaintenanceRecurrence | null;
  timezone?: string;
  targets?: NotificationTarget[];
}

export interface ConfigExport {
  version: string;
  exported_at: string;
  include_secrets?: boolean;
  services: Service[];
  groups: Group[];
  notifications: NotificationChannel[];
  /** Pre-1.2 exports: generic webhooks, imported as webhook-type channels. */
  webhooks?: unknown[];
}

export type ImportAction = 'create' | 'update' | 'skip';

export interface ImportSection {
  created: number;
  updated: number;
  skipped: number;
  items: { name: string; action: ImportAction; reason?: string }[];
}

export interface ImportResult {
  dry_run: boolean;
  groups: ImportSection;
  services: ImportSection;
  notifications: ImportSection;
  credentials_required: {
    services: { name: string; reason: string }[];
    notifications: { name: string; reason: string }[];
  };
}

export interface AuditLogPage {
  data: AuditLogEntry[];
  total: number;
}

export interface AuditFacets {
  actions: string[];
  resource_types: string[];
  users: { id: number; name: string; email: string }[];
}

export interface AuthOptions {
  registration_enabled: boolean;
}

// Auth types
export interface User {
  id: number;
  email: string;
  name: string;
  role_id: number;
  role: string;
  permissions?: string[];
  status: 'active' | 'inactive' | 'locked';
  locked_until?: string | null;
  created_at: string;
  updated_at: string;
  isGuest?: boolean;
}

export interface GuestPasscode {
  id: number;
  code: string;
  label: string;
  created_by: number;
  created_by_name?: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  use_count: number;
  created_at: string;
}

export interface Role {
  id: number;
  name: string;
  description: string | null;
  permissions: string[];
  is_system: boolean;
}

export interface Session {
  id: string;
  user_id: number;
  ip_address: string | null;
  user_agent: string | null;
  remember_me: boolean;
  expires_at: string;
  last_active_at: string;
  created_at: string;
}

export interface AuditLogEntry {
  id: number;
  user_id: number | null;
  user_name?: string | null;
  user_email?: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface Incident {
  id: number;
  service_id: number;
  service_name: string;
  type: 'down' | 'degraded';
  previous_status: ServiceStatus;
  new_status: ServiceStatus;
  message: string;
  started_at: string;
  resolved_at: string | null;
  duration_seconds: number | null;
  ongoing: boolean;
  acknowledged_at: string | null;
  acknowledged_by_name: string | null;
  note: string | null;
  /** Present state of the episode (down or degraded) while it is open. */
  current_status: ServiceStatus | null;
  /** Times the service went down again inside the flap window and re-used this incident. */
  reopened_count: number;
  flapping: boolean;
  reminder_count: number;
  escalated_at: string | null;
  /** Shown on status pages that carry the service. */
  public: boolean;
  public_title: string | null;
}

export interface IncidentSummary {
  window_days: number;
  total: number;
  ongoing: number;
  acknowledged: number;
  resolved: number;
  mttr_seconds: number | null;
  longest_seconds: number | null;
  total_downtime_seconds: number;
  worst_service: { service_id: number; service_name: string; count: number } | null;
}

export interface AdHocTestResult {
  ok: boolean;
  type: ServiceType;
  status: ServiceStatus;
  status_code: number | null;
  response_time: number | null;
  error_message: string | null;
  details: CheckDetails | null;
  expected_status: number;
}

// ── Status pages (public payload) ────────────────────────────────────────────

export interface DailyCell {
  date: string;
  uptime: number | null;
  status: 'up' | 'degraded' | 'down' | 'maintenance' | null;
  incidents: number;
}

export interface PublicStatusService {
  id: number;
  name: string;
  description: string | null;
  section_id: number | null;
  status: ServiceStatus;
  uptime_24h: number | null;
  uptime_7d: number | null;
  uptime_30d: number | null;
  uptime_90d: number | null;
  daily: DailyCell[];
  last_checked_at: string | null;
  avg_response_24h?: number | null;
}

export type IncidentUpdateStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved';

export interface PublicIncident {
  id: number;
  service_id: number;
  service_name: string;
  status: 'down' | 'degraded';
  title: string;
  started_at: string;
  resolved_at: string | null;
  ongoing: boolean;
  updates: { status: IncidentUpdateStatus | null; title: string; text: string; created_at: string }[];
}

export interface PublicAnnouncement {
  id: number;
  title: string;
  text: string;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
}

export interface PublicMaintenance {
  id: number;
  name: string;
  description: string | null;
  active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  schedule: string | null;
  timezone: string;
  services: string[];
}

export interface PublicStatus {
  page: {
    slug: string;
    title: string;
    description: string | null;
    logo_url: string | null;
    favicon_url: string | null;
    accent_color: string | null;
    custom_css: string | null;
    footer_text: string | null;
    support_url: string | null;
    show_response_times: boolean;
  };
  title: string;
  overall: ServiceStatus;
  sections: { id: number; name: string; description: string | null }[];
  services: PublicStatusService[];
  incidents: PublicIncident[];
  announcements: PublicAnnouncement[];
  maintenance: PublicMaintenance[];
  links: { feed: string; embed: string; page: string };
  generated_at: string;
}

// ── Status pages (admin) ─────────────────────────────────────────────────────

export type StatusPageAccess = 'public' | 'passcode' | 'authenticated';

export interface StatusPage {
  id: number;
  slug: string;
  title: string;
  description: string | null;
  enabled: boolean;
  is_default: boolean;
  access: StatusPageAccess;
  passcode_id: number | null;
  logo_url: string | null;
  favicon_url: string | null;
  accent_color: string | null;
  custom_css: string | null;
  footer_text: string | null;
  support_url: string | null;
  incident_days: number;
  show_response_times: boolean;
  created_at: string;
  updated_at: string;
  /** List view. */
  service_count?: number;
  url?: string;
}

export interface StatusPageSection {
  id: number;
  page_id: number;
  name: string;
  description: string | null;
  sort_order: number;
}

export interface StatusPageServiceRow {
  page_id: number;
  service_id: number;
  section_id: number | null;
  sort_order: number;
  display_name: string | null;
  description: string | null;
  service_name: string;
  service_type: ServiceType;
  service_status: ServiceStatus;
  service_enabled: boolean;
}

export interface StatusPageDetail extends StatusPage {
  sections: StatusPageSection[];
  services: StatusPageServiceRow[];
  passcode_label: string | null;
  url: string;
}

export type StatusPageInput = Partial<Omit<StatusPage, 'id' | 'created_at' | 'updated_at' | 'is_default' | 'service_count' | 'url'>>;

export interface StatusPageLayoutInput {
  sections: { id?: number; name: string; description?: string | null }[];
  services: { service_id: number; section?: number | null; display_name?: string | null; description?: string | null }[];
}

export interface StatusPost {
  id: number;
  page_id: number | null;
  incident_id: number | null;
  kind: 'announcement' | 'update';
  title: string;
  body: string;
  status: IncidentUpdateStatus | null;
  published: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface InstanceSettings {
  'status_pages.enabled': boolean;
  'status_pages.title': string;
}

// Reports

export type PercentileSource = 'raw-exact' | 'hourly-estimate' | null;

export interface ReportMeta {
  source: {
    uptime: 'hourly';
    response_mean: 'hourly-exact' | 'hourly-approx';
    percentiles: PercentileSource;
  };
  coverage: { hours_expected: number; hours_present: number };
  range: { from: string; to: string };
  generated_at: string;
}

export interface HourOfDayRow {
  hour_of_day: string;      // '00'..'23', UTC
  uptime: number | null;
  mean_response: number | null;
}

export interface ServiceReportRow {
  service_id: number;
  service_name: string;
  uptime: number | null;            // null when no data — never 100
  grade: 'A' | 'B' | 'C' | 'D' | 'F' | null;
  sla_target: number;
  sla_target_inherited: boolean;
  error_budget_consumed: number | null;
  error_budget_remaining_minutes: number | null;
  incident_count: number;
  ongoing_count: number;
  mttr_seconds: number | null;
  mtbf_seconds: number | null;
  longest_seconds: number | null;
  downtime_seconds: number;
  mean_response: number | null;
  min_response: number | null;
  max_response: number | null;
  p50: number | null;
  p95: number | null;
  p99: number | null;
  worst_hour_p95: number | null;
  hours_present: number;
  hours_expected: number;
  uptime_delta?: number | null;     // present only with compare=previous
  incident_delta?: number | null;
  mttr_delta?: number | null;
  trend?: 'rising' | 'falling' | 'flat';
}

export interface FleetReport { rows: ServiceReportRow[]; meta: ReportMeta; }
export interface Scorecard {
  current: ServiceReportRow;
  previous: ServiceReportRow | null;
  hour_of_day: HourOfDayRow[];
  meta: ReportMeta;
}

// ── Phase 4: integrations ────────────────────────────────────────────────────

export type ApiKeyScope = 'read' | 'write';

export interface ApiKey {
  id: number;
  user_id: number;
  name: string;
  /** First characters of the token, e.g. `pc_3f9a1c2b` — the token itself is shown once. */
  prefix: string;
  scope: ApiKeyScope;
  service_ids: number[] | null;
  expires_at: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  revoked_at: string | null;
  created_at: string;
  user_email?: string;
  user_name?: string;
}

export interface ApiKeyCreated extends ApiKey {
  token: string;
}

export interface ApiKeyInput {
  name: string;
  scope: ApiKeyScope;
  service_ids?: number[];
  expires_in_days?: number;
}

export type BulkAction = 'pause' | 'resume' | 'delete' | 'add_tags' | 'remove_tags' | 'add_to_group' | 'remove_from_group' | 'set_interval' | 'check';

export interface BulkInput {
  ids: number[];
  action: BulkAction;
  tags?: string[];
  group_id?: number;
  interval?: number;
}

export interface BulkResult {
  action: BulkAction;
  applied: number;
  results: { id: number; name: string | null; ok: boolean; error?: string }[];
}

export interface ServiceTemplate {
  id: number;
  name: string;
  description: string | null;
  payload: Partial<ServiceInput>;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface BackupFile {
  name: string;
  path: string;
  size_bytes: number;
  created_at: string;
}

export interface BackupResult {
  file: BackupFile;
  duration_ms: number;
  uploaded_to: string | null;
  pruned: string[];
}

export interface BackupStatus {
  enabled: boolean;
  schedule: string;
  dir: string;
  keep: number;
  s3: { configured: boolean; bucket: string | null; endpoint: string | null };
  last_run_at: string | null;
  last_result: BackupResult | null;
  last_error: string | null;
  files: BackupFile[];
}

/** Days; 0 = keep forever. */
export interface RetentionPolicy {
  checks_days: number;
  alerts_days: number;
  hourly_days: number;
  deliveries_days: number;
  audit_days: number;
  audit_access_days: number;
}

export interface ConfigFileState {
  path: string | null;
  exists: boolean;
  watching: boolean;
  last_loaded_at: string | null;
  last_result: ImportResult | null;
  last_error: string | null;
  missing_env: string[];
}

export interface PushSubscriptionInfo {
  id: number;
  user_id: number;
  endpoint: string;
  user_agent: string | null;
  created_at: string;
  last_used_at: string | null;
}
